import qs from 'qs';

import { v1ApiClient } from '@/services';
import { getCurrWorkspaceAlias } from '@/utils/workspace';

import type {
  DraftScrapingJob,
  SCRAPING_JOB_STATUS,
  SCRAPING_JOB_TYPE,
} from '../types';
import type {
  CheckExistsScrapingJobRequest,
  CheckExistsScrapingJobResponse,
  CreateScrapingJobRequest,
  RetrieveScrapingJobsRequestParams,
  RetrieveScrapingJobsResponse,
  ScrapingJob,
  ScrapingJobStatsResponse,
  ValidateScrapingJobsResponse,
} from './dto';

export const scrapingService = {
  retrieveScrapingJob: async (jobId: string): Promise<ScrapingJob> => {
    const workspace_id = getCurrWorkspaceAlias();
    const res = await v1ApiClient.get<ScrapingJob>(
      `/workspaces/${workspace_id}/scraping-jobs/${jobId}`,
    );
    return res.data;
  },
  retrieveScrapingJobs: async (
    params: RetrieveScrapingJobsRequestParams,
  ): Promise<RetrieveScrapingJobsResponse> => {
    const workspace_id = getCurrWorkspaceAlias();
    const res = await v1ApiClient.get<RetrieveScrapingJobsResponse>(
      `/workspaces/${workspace_id}/scraping-jobs`,
      {
        params: {
          limit: params.limit,
          offset: params.offset,
          status: params.status,
          type: params.type,
        },
        paramsSerializer: (params) =>
          qs.stringify(params, { arrayFormat: 'repeat' }),
      },
    );

    return res.data;
  },
  retrieveScrapingJobAggregate: async (
    type: SCRAPING_JOB_TYPE,
    status: SCRAPING_JOB_STATUS[],
  ) => {
    const workspace_id = getCurrWorkspaceAlias();
    const res = await v1ApiClient.get<ScrapingJobStatsResponse>(
      `/workspaces/${workspace_id}/scraping-jobs/aggregate`,
      {
        params: {
          type,
          status,
        },
        paramsSerializer: (params) =>
          qs.stringify(params, { arrayFormat: 'repeat' }),
      },
    );
    return res.data;
  },
  createScrapingJob: async (
    params: CreateScrapingJobRequest,
  ): Promise<ScrapingJob[]> => {
    const workspace_id = getCurrWorkspaceAlias();
    const res = await v1ApiClient.post(
      `/workspaces/${workspace_id}/scraping-jobs`,
      params,
    );
    return res.data;
  },
  updatedScrapingJob: async (alias: string, params: DraftScrapingJob) => {
    const workspace_id = getCurrWorkspaceAlias();
    const res = await v1ApiClient.put(
      `/workspaces/${workspace_id}/scraping-jobs/${alias}`,
      params,
    );
    return res.data;
  },
  deleteScrapingJob: async (aliases: string[]) => {
    const workspace_id = getCurrWorkspaceAlias();
    const res = await v1ApiClient.delete(
      `/workspaces/${workspace_id}/scraping-jobs`,
      {
        data: {
          scraping_job_ids: aliases,
        },
      },
    );
    return res.data;
  },
  validateScrapingJob: async (params: CreateScrapingJobRequest) => {
    const workspace_id = getCurrWorkspaceAlias();
    const res = await v1ApiClient.post<ValidateScrapingJobsResponse>(
      `/workspaces/${workspace_id}/scraping-jobs/validate`,
      params,
    );
    return res.data;
  },
  checkExistsScrapingJob: async (params: CheckExistsScrapingJobRequest) => {
    const workspace_id = getCurrWorkspaceAlias();
    const res = await v1ApiClient.post<CheckExistsScrapingJobResponse>(
      `/workspaces/${workspace_id}/scraping-jobs/exists`,
      params,
    );
    return res.data;
  },
  confirmScrapingJob: async (alias: string) => {
    const workspace_id = getCurrWorkspaceAlias();
    const res = await v1ApiClient.patch(
      `/workspaces/${workspace_id}/scraping-jobs/${alias}/confirm`,
    );
    return res.data;
  },
  cancelScrapingJob: async (aliases: string[]) => {
    const workspace_id = getCurrWorkspaceAlias();
    const res = await v1ApiClient.patch(
      `/workspaces/${workspace_id}/scraping-jobs/cancel`,
      {
        scraping_job_ids: aliases,
      },
    );
    return res.data;
  },
  deleteFeedbackByScrapingJob: async (alias: string, cause: string) => {
    const workspace_id = getCurrWorkspaceAlias();
    const res = await v1ApiClient.delete(
      `/workspaces/${workspace_id}/scraping-jobs/${alias}/feedbacks`,
      {
        data: {
          cause,
        },
      },
    );
    return res.data;
  },
};
